import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../../config";
import { CheckCircle, XCircle, Search, ChevronDown, ChevronUp, RefreshCw, Unlock, Edit } from "lucide-react";

const IeltsResultManager = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [filter, setFilter] = useState({ subject_id: "", status: "" });
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingWriting, setEditingWriting] = useState(null);
  const [writingValue, setWritingValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };

  useEffect(() => {
    loadSubjects();
    loadResults();
  }, []);

  const loadSubjects = async () => {
    try {
      const res = await axios.get(`${config.apiUrl}/ielts-subjects`, { headers });
      setSubjects(res.data || []);
    } catch {}
  };

  const loadResults = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.subject_id) params.subject_id = filter.subject_id;
      if (filter.status) params.status = filter.status;
      const res = await axios.get(`${config.apiUrl}/admin/ielts-results`, { headers: { ...headers, params } });
      setResults(res.data.data || res.data || []);
    } catch {} finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (id) => {
    if (!window.confirm("Unlock this exam? Student will be able to retake it.")) return;
    await axios.post(`${config.apiUrl}/admin/ielts-results/${id}/unlock`, {}, { headers });
    setMessage("Exam unlocked.");
    loadResults();
  };

  const handleReset = async (id) => {
    if (!window.confirm("Reset this exam? All scores and answers will be cleared.")) return;
    await axios.post(`${config.apiUrl}/admin/ielts-results/${id}/reset`, {}, { headers });
    setMessage("Exam reset.");
    loadResults();
  };

  const handleSaveWriting = async (id) => {
    if (!writingValue) return;
    setSaving(true);
    try {
      await axios.post(`${config.apiUrl}/admin/ielts-results/${id}/writing-score`, { writing_band: writingValue }, { headers });
      setMessage("Writing band saved.");
      setEditingWriting(null);
      setWritingValue("");
      loadResults();
    } catch (e) {
      setMessage(e.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const BandBadge = ({ band, label }) => (
    <span className={`inline-flex items-center justify-center rounded-lg px-2.5 py-1 text-sm font-black ${
      band >= 8 ? "bg-green-100 text-green-700" : band >= 6.5 ? "bg-blue-100 text-blue-700" : band >= 5.5 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600"
    }`}>
      {band ?? "—"}
      {label && <span className="ml-1 text-xs font-normal opacity-70">{label}</span>}
    </span>
  );

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">IELTS Results</h1>
          <p className="text-sm text-gray-500">View student scores, set writing bands, and manage exam locks.</p>
        </div>
        <button onClick={loadResults} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white hover:bg-black transition-colors">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {message && (
        <div className="rounded-lg border px-4 py-3 text-sm bg-green-50 border-green-200 text-green-800">
          {message}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <select
          value={filter.subject_id}
          onChange={(e) => setFilter((f) => ({ ...f, subject_id: e.target.value }))}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
        >
          <option value="">All Subjects</option>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          value={filter.status}
          onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="evaluated">Evaluated</option>
        </select>
        <button onClick={loadResults} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 transition-colors">
          <Search className="h-4 w-4 inline mr-1" /> Filter
        </button>
      </div>

      {/* Results table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Student</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Subject</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-600">Listening</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-600">Reading</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-600">Writing</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-600">Overall</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-600">Status</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-600">Lock</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-500">Loading…</td></tr>
              ) : results.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">No results found.</td></tr>
              ) : results.map((r) => {
                const isExpanded = expandedRow === r.id;
                const isEditing = editingWriting === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isExpanded ? "bg-indigo-50" : ""}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-semibold text-gray-900">{r.user?.name || "—"}</p>
                          <p className="text-xs text-gray-500">{r.user?.email || ""}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800">{r.subject?.name || "IELTS"}</p>
                        {r.submitted_at && (
                          <p className="text-[10px] text-gray-400">{new Date(r.submitted_at).toLocaleDateString("en-BD")}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <BandBadge band={r.listening_band} />
                        {r.listening_score !== null && (
                          <p className="text-[10px] text-gray-400 mt-0.5">{r.listening_score}/40</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <BandBadge band={r.reading_band} />
                        {r.reading_score !== null && (
                          <p className="text-[10px] text-gray-400 mt-0.5">{r.reading_score}/40</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="0" max="9" step="0.5"
                              value={writingValue}
                              onChange={(e) => setWritingValue(e.target.value)}
                              className="w-16 rounded border border-gray-300 px-2 py-1 text-sm text-center focus:border-indigo-500 focus:outline-none"
                              placeholder="0-9"
                            />
                            <button
                              onClick={() => handleSaveWriting(r.id)}
                              disabled={saving}
                              className="rounded bg-green-600 px-2 py-1 text-xs font-bold text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => { setEditingWriting(null); setWritingValue(""); }}
                              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50"
                            >✕</button>
                          </div>
                        ) : (
                          <BandBadge band={r.writing_band} label="W" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.overall_band ? (
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 text-sm font-black text-white">
                            {r.overall_band}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-500">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          r.exam_status === "evaluated" ? "bg-green-100 text-green-700"
                          : r.exam_status === "completed" ? "bg-blue-100 text-blue-700"
                          : "bg-amber-100 text-amber-700"
                        }`}>
                          {r.exam_status || "pending"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${
                          r.is_locked ? "text-red-600" : "text-green-600"
                        }`}>
                          {r.is_locked ? "🔒 Locked" : "✅ Open"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center gap-1 justify-center">
                          {!isEditing && (
                            <button
                              onClick={() => {
                                setEditingWriting(r.id);
                                setWritingValue(r.writing_band?.toString() || "");
                              }}
                              className="rounded border border-indigo-200 px-2 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors"
                              title="Set writing band"
                            >
                              <Edit className="h-3 w-3" />
                            </button>
                          )}
                          {!r.is_locked ? (
                            <button
                              onClick={() => handleUnlock(r.id)}
                              className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
                              title="Unlock exam"
                            >
                              <Unlock className="h-3 w-3" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReset(r.id)}
                              className="rounded border border-red-200 px-2 py-1 text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors"
                              title="Reset exam"
                            >
                              <RefreshCw className="h-3 w-3" />
                            </button>
                          )}
                          <button
                            onClick={() => setExpandedRow(isExpanded ? null : r.id)}
                            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
                          >
                            {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded detail row */}
                    {isExpanded && (
                      <tr className="bg-indigo-50/50">
                        <td colSpan={9} className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Listening answers */}
                            <div className="bg-white rounded-xl border border-gray-200 p-4">
                              <h4 className="font-bold text-gray-900 text-sm mb-3">Listening — Answer Review</h4>
                              {r.listening_answers?.listening ? (
                                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                  {Object.entries(r.listening_answers.listening).map(([num, info]) => (
                                    <div key={num} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2">
                                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gray-200 text-[10px] font-bold text-gray-600">
                                        {num}
                                      </span>
                                      {info.is_correct ? (
                                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                                      ) : (
                                        <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                                      )}
                                      <span className={`flex-1 text-xs font-medium ${info.is_correct ? "text-green-700" : "text-red-600"}`}>
                                        {info.student_answer || <span className="italic text-gray-400">Skipped</span>}
                                      </span>
                                      {!info.is_correct && info.correct_answer && (
                                        <span className="text-xs font-semibold text-green-600 shrink-0">
                                          → {info.correct_answer}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400">No listening data.</p>
                              )}
                            </div>

                            {/* Reading answers */}
                            <div className="bg-white rounded-xl border border-gray-200 p-4">
                              <h4 className="font-bold text-gray-900 text-sm mb-3">Reading — Answer Review</h4>
                              {r.reading_answers?.reading ? (
                                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                  {Object.entries(r.reading_answers.reading).map(([num, info]) => (
                                    <div key={num} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2">
                                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gray-200 text-[10px] font-bold text-gray-600">
                                        {num}
                                      </span>
                                      {info.is_correct ? (
                                        <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                                      ) : (
                                        <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                                      )}
                                      <span className={`flex-1 text-xs font-medium ${info.is_correct ? "text-green-700" : "text-red-600"}`}>
                                        {info.student_answer || <span className="italic text-gray-400">Skipped</span>}
                                      </span>
                                      {!info.is_correct && info.correct_answer && (
                                        <span className="text-xs font-semibold text-green-600 shrink-0">
                                          → {info.correct_answer}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400">No reading data.</p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IeltsResultManager;